import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  'Motion & Animation',
  'Thumbnail & Image Generation',
  'Consultancy',
  'Creative Direction',
  'Full Campaign',
];

const packages = [
  { id: 'silver', name: 'Silver', price: '£495', description: 'Single Animated Custom 3D Scene 20 Seconds' },
  { id: 'gold', name: 'Gold', price: '£1,095', description: 'Three Custom 3D Animated Scenes 20 Seconds Each' },
  { id: 'platinum', name: 'Platinum', price: '£1,995', description: 'Complete Production of Six Custom 3D Animated Scenes 20 Seconds Each + 10 Seconds Free Bonus Scene' },
];

export default function ConsultationSection() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', project_type: '', message: '', package_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.project_type) {
      setError('Please fill in all required fields (Name, Email, Project Type).');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/bookings`, form);
      const booking = res.data;

      if (form.package_id) {
        // Create Stripe checkout
        const originUrl = window.location.origin;
        const checkoutRes = await axios.post(`${API}/checkout`, {
          booking_id: booking.id,
          package_id: form.package_id,
          origin_url: originUrl,
        });
        // Redirect to Stripe
        window.location.href = checkoutRes.data.url;
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="consultation" className="section-padding" data-testid="consultation-section">
        <div className="section-divider mb-24" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-16 border border-white/20 flex items-center justify-center mx-auto mb-8">
              <Check size={28} className="text-white" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl mb-4" data-testid="booking-success-title">CONSULTATION BOOKED</h2>
            <p className="text-white/40 text-base mb-6">
              We'll be in touch within 24 hours at archangelworks1@gmail.com.
            </p>
            <a
              href="https://calendly.com/archangelworks1/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-xs inline-block"
            >
              Schedule a Call Now
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="consultation" className="section-padding overflow-hidden" data-testid="consultation-section">
      <div className="section-divider mb-24" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-mono text-xs tracking-[0.3em] text-white/30 uppercase mb-4">Get Started</p>
            <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl mb-6">
              BOOK A FREE
              <br />
              <span className="text-white/25">CONSULTATION</span>
            </h2>
            <p className="text-white/35 text-base leading-relaxed mb-12">
              Tell us about your vision. We'll show you how we can bring it to life
              with AI-powered 3D that sets your brand apart.
            </p>

            {/* Packages */}
            <div className="space-y-3">
              <p className="font-mono text-xs tracking-[0.2em] text-white/30 uppercase mb-4">Optional Deposit Package</p>
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handleChange('package_id', form.package_id === pkg.id ? '' : pkg.id)}
                  className={`w-full text-left p-5 border transition-all duration-300 ${
                    form.package_id === pkg.id
                      ? 'border-white/40 bg-white/5'
                      : 'border-white/8 hover:border-white/15 bg-transparent'
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-heading text-sm">{pkg.name}</span>
                      <p className="text-white/30 text-xs mt-1">{pkg.description}</p>
                    </div>
                    <span className="font-mono text-sm text-white/60">{pkg.price}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="consultation-form">
              <div>
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input-minimal"
                  data-testid="input-name"
                  required
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="input-minimal"
                  data-testid="input-email"
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="input-minimal"
                  data-testid="input-company"
                />
              </div>
              <div>
                <select
                  value={form.project_type}
                  onChange={(e) => handleChange('project_type', e.target.value)}
                  className="input-minimal bg-transparent appearance-none cursor-pointer"
                  data-testid="select-project-type"
                  required
                >
                  <option value="" disabled className="bg-black text-white/40">Project Type *</option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type} className="bg-black text-white">{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <textarea
                  placeholder="Tell us about your project..."
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows={4}
                  className="input-minimal resize-none"
                  data-testid="input-message"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm font-mono" data-testid="form-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-3"
                data-testid="submit-consultation"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    {form.package_id ? 'Book & Pay Deposit' : 'Book Free Consultation'}
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
